import React, { useState } from 'react';
import { Twitter, RefreshCw, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { getUserApiCredentials } from '../lib/apiCredentials';
import { supabase } from '../lib/supabase';

interface XConnectProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  hasConnectedAccounts?: boolean;
}

export function XConnect({ onSuccess, onError, hasConnectedAccounts }: XConnectProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleXLogin = async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        setIsLoading(false);
        return;
      }

      const credentials = await getUserApiCredentials();
      const clientId = credentials.x_api_key;

      if (!clientId) {
        toast.error('X API Key not configured. Please add your credentials in API Settings.');
        setIsLoading(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = `${supabaseUrl}/functions/v1/x-oauth`;

      localStorage.setItem('social_auth_redirect', window.location.href);

      const codeChallenge = 'challenge';
      const scopes = [
        'tweet.read',
        'tweet.write',
        'users.read',
        'offline.access',
      ];

      const authUrl = new URL('https://twitter.com/i/oauth2/authorize');
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('state', user.id);
      authUrl.searchParams.set('scope', scopes.join(' '));
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'plain');

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('X login error:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to connect X account');
      toast.error('Failed to connect X account');
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      <button
        onClick={handleXLogin}
        disabled={isLoading}
        className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg
          text-gray-500 hover:text-black hover:border-black w-full justify-center
          disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? (
          <RefreshCw className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Twitter className="h-5 w-5" />
            <span>{hasConnectedAccounts ? 'Connect Another X Account' : 'Connect X Account'}</span>
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-2 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1">
        <AlertCircle className="w-3 h-3" />
        <span>X API requires paid plan: Basic tier $200/month for comment access</span>
      </div>
    </div>
  );
}
